const PREFERENCE_KEY = 'omicsbox-session-expiration-preference'
const ANCHOR_KEY = 'omicsbox-session-anchor-ms'

/** Cognito custom attribute — **Number**, hours (0 = no extra sign-out limit from this setting). */
export const COGNITO_SESSION_EXPIRATION_ATTR = 'custom:session_expiration'

/** Upper bound ~10 years; matches long refresh-token horizons and keeps the attribute bounded. */
export const MAX_SESSION_EXPIRATION_HOURS = 365 * 24 * 10

const MS_PER_HOUR = 60 * 60 * 1000

const LEGACY_STRING_TO_HOURS: Record<string, number> = {
  never: 0,
  '24h': 24,
  '30d': 30 * 24,
}

/**
 * Parses Cognito/localStorage value: integer hours, or legacy `never` / `24h` / `30d` strings.
 */
export function parseSessionExpirationHours(raw: string | undefined | null): number {
  if (raw == null || raw === '') {
    return 0
  }
  const trimmed = String(raw).trim()
  if (trimmed in LEGACY_STRING_TO_HOURS) {
    return LEGACY_STRING_TO_HOURS[trimmed]
  }
  const n = Number.parseInt(trimmed, 10)
  if (Number.isNaN(n) || n < 0) {
    return 0
  }
  return Math.min(n, MAX_SESSION_EXPIRATION_HOURS)
}

export function normalizeSessionExpirationHours(n: number | undefined | null): number {
  if (n == null || Number.isNaN(n)) {
    return 0
  }
  const x = Math.floor(n)
  if (x < 0) {
    return 0
  }
  if (x > MAX_SESSION_EXPIRATION_HOURS) {
    return MAX_SESSION_EXPIRATION_HOURS
  }
  return x
}

export function getSessionExpirationHours(): number {
  return parseSessionExpirationHours(localStorage.getItem(PREFERENCE_KEY))
}

export function setSessionExpirationHours(hours: number): void {
  localStorage.setItem(PREFERENCE_KEY, String(normalizeSessionExpirationHours(hours)))
}

/**
 * True when Cognito rejected updating session duration (app client not allowed to write this
 * custom attribute, or attribute missing from pool schema).
 */
export function isSessionExpirationCognitoWriteForbidden(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  return (
    msg.includes('NotAuthorizedException') &&
    (msg.includes('unauthorized attribute') ||
      msg.includes('A client attempted to write unauthorized attribute'))
  )
}

/** Mirrors Cognito hours into localStorage when the user has a stored pool value. */
export function syncSessionPreferenceFromCognito(value: number | undefined): void {
  if (value === undefined) {
    return
  }
  setSessionExpirationHours(normalizeSessionExpirationHours(value))
}

export function setSessionAnchorNow(): void {
  localStorage.setItem(ANCHOR_KEY, String(Date.now()))
}

export function ensureSessionAnchorIfMissing(): void {
  if (!localStorage.getItem(ANCHOR_KEY)) {
    setSessionAnchorNow()
  }
}

export function clearSessionAnchor(): void {
  localStorage.removeItem(ANCHOR_KEY)
}

export function isSessionExpiredByPolicy(): boolean {
  const hours = getSessionExpirationHours()
  if (hours === 0) {
    return false
  }
  const raw = localStorage.getItem(ANCHOR_KEY)
  if (!raw) {
    return false
  }
  const anchor = Number(raw)
  if (Number.isNaN(anchor)) {
    return false
  }
  return Date.now() - anchor > hours * MS_PER_HOUR
}
